import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, take, throwError } from 'rxjs';

import { PaywallErrorDetails, SubscriptionTier, tierAtLeast } from '../../api/subscriptions/subscriptions.model';
import { ErrorType } from '../../enums/error-type.enum';
import { PaywallService } from '../services/paywall.service';

/**
 * Retries a request denied for `PRO_SUBSCRIPTION_REQUIRED` once the paywall state is confirmed
 * fresh, and otherwise routes to the upgrade page with the tier that would have satisfied it.
 *
 * Only `PRO_SUBSCRIPTION_REQUIRED` is handled here. `USAGE_LIMIT_EXCEEDED` (429, a spent meter)
 * and `ACCOUNT_LIMIT_EXCEEDED` (402, a full row cap) are deliberately left alone — neither is a
 * locked feature, so upselling would be wrong; they propagate untouched for feature surfaces to
 * read `error.error.details` themselves (typed as `UsageLimitErrorDetails` /
 * `AccountLimitErrorDetails`).
 */
export const paywallInterceptor: HttpInterceptorFn = (req, next) => {
  const paywall = inject(PaywallService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.error?.errorType !== ErrorType.ProSubscriptionRequired) {
        return throwError(() => error);
      }

      // `details` may be absent on old cached error responses. An unknown requiredTier can never
      // be satisfied, so it falls straight through to the "route without preselection" branch.
      const details: PaywallErrorDetails | undefined = error.error?.details;
      const requiredTier: SubscriptionTier | null = details?.requiredTier ?? null;

      return paywall.load().pipe(
        take(1),
        switchMap(() => {
          // Retry only when the operator's tier genuinely satisfies the requirement — i.e. the
          // paywall state we had was stale. A Starter operator hitting a Pro-only feature must
          // never be retried into failing again.
          const satisfied = !!requiredTier && tierAtLeast(paywall.currentTier(), requiredTier);
          if (satisfied) return next(req);

          router.navigate(
            ['/subscription/upgrade'],
            requiredTier ? { queryParams: { requiredTier } } : undefined
          );
          return throwError(() => error);
        })
      );
    })
  );
};
